import { createLogger } from './logger';

/**
 * API Client with retry logic and configurable base URL.
 */

// ============================================================
// Types
// ============================================================

/**
 * Extended request options including retry configuration.
 */
export interface RequestOptions extends RequestInit {
  /** Maximum number of retries (default: 3) */
  maxRetries?: number;
}

/**
 * Fetch implementation type.
 */
type FetchImpl = typeof fetch;

// ============================================================
// Configuration
// ============================================================

/**
 * Get the default base URL based on environment.
 * In production (non-localhost), uses same origin.
 * In development, defaults to localhost:3001.
 */
function getDefaultBaseURL(): string {
  // Check if we're running in a browser
  if (typeof window !== 'undefined' && window.location) {
    const { protocol, hostname, port } = window.location;
    
    // If not localhost, we're in production - use same origin
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `${protocol}//${hostname}${port ? ':' + port : ''}`;
    }
  }
  
  // Development default
  return 'http://localhost:3001';
}

let baseURL = getDefaultBaseURL();

// Default retry settings
const DEFAULT_MAX_RETRIES = 3;
const INITIAL_BACKOFF_MS = 100;

// Test hook for mock fetch
let fetchImpl: FetchImpl | null = null;

const log = createLogger('api-client');

// ============================================================
// Test Hooks
// ============================================================

/**
 * Test hook to inject a mock fetch implementation.
 */
export function _setFetchImpl(impl: FetchImpl | null): void {
  fetchImpl = impl;
}

// ============================================================
// Utility Functions
// ============================================================

/**
 * Sleep for a given duration.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if an HTTP status code is retryable (5xx server errors).
 */
function isRetryableStatus(status: number): boolean {
  return status >= 500 && status < 600;
}

// ============================================================
// Public API
// ============================================================

/**
 * Set the base URL for API requests.
 * @param nextBaseURL - The new base URL (must be non-empty string)
 * @throws Error if nextBaseURL is not a non-empty string
 */
export function setBaseURL(nextBaseURL: string): void {
  if (typeof nextBaseURL !== 'string' || nextBaseURL.trim().length === 0) {
    throw new Error('baseURL must be a non-empty string');
  }
  // Normalize trailing slash
  baseURL = nextBaseURL.trim().replace(/\/+$/, '');
}

/**
 * Get the current base URL.
 */
export function getBaseURL(): string {
  return baseURL;
}

/**
 * Make an HTTP request with automatic retry logic.
 *
 * - Retries on network errors with exponential backoff
 * - Retries on 5xx server errors with exponential backoff
 * - Does NOT retry on 4xx client errors (permanent failures)
 *
 * @param endpoint - API endpoint (relative to baseURL) or full URL
 * @param options - Request options including maxRetries
 * @returns Response object
 * @throws Error on permanent failure or after all retries exhausted
 */
export async function request(
  endpoint: string,
  options: RequestOptions = {}
): Promise<Response> {
  const url = endpoint.startsWith('http://') || endpoint.startsWith('https://')
    ? endpoint
    : `${baseURL}${endpoint}`;

  const { maxRetries = DEFAULT_MAX_RETRIES, ...fetchOptions } = options;
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...fetchOptions.headers
    },
    ...fetchOptions
  };

  const doFetch = fetchImpl || fetch;
  let lastError: Error | null = null;

  // Attempt initial request + retries
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await doFetch(url, config);
      
      if (!response.ok) {
        // 4xx errors are client errors - don't retry
        if (response.status >= 400 && response.status < 500) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        // 5xx errors are retryable
        if (isRetryableStatus(response.status) && attempt < maxRetries) {
          const backoff = INITIAL_BACKOFF_MS * Math.pow(2, attempt);
          await sleep(backoff);
          continue;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      lastError = error as Error;
      
      // If it's an HTTP error (not network), and we already threw above, rethrow
      if (lastError.message?.startsWith('HTTP error!')) {
        throw lastError;
      }
      
      // Network error - retry with backoff if attempts remain
      if (attempt < maxRetries) {
        const backoff = INITIAL_BACKOFF_MS * Math.pow(2, attempt);
        await sleep(backoff);
        continue;
      }
    }
  }

  // All retries exhausted
  log.error(`API request failed for ${endpoint} after ${maxRetries + 1} attempts:`, lastError);
  throw lastError;
}

